import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetCalendarMeetingLocationsQuery, useGetMeetingCountriesQuery } from '../../api/misc.js';
import BaseInput from '../../components/form/BaseInput.jsx';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import { useDebounce } from '../../hooks/useDebounce.js';
import circle from '../../resources/images/circle.png';
import BaseCard from '../../components/misc/BaseCard.jsx';
import AdvancedSearchFilter from '../../components/misc/AdvancedSearchFilter.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import { useLazyCalendarMeetingAdvancedSearchQuery } from '../../api/calendar.js';
import { useSelector } from 'react-redux';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { renderFieldWithHighlighting } from '../../utils/highlight.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';

const AdvancedSearchCalendarMeetings = () => {
    const organizationSwitch = useSelector((state) => state.organizationSwitch);
    const { user } = useCurrentUser();

    const [isLoading, setIsLoading] = useState(false);
    const [initialRender, setInitialRender] = useState(true);
    const [meetings, setMeetings] = useState([]);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({
        date: null,
        locations: [],
        countries: []
    });
    const [applied, setApplied] = useState(false);
    const debouncedSearch = useDebounce(search, 500);
    const [showFilters, setShowFilters] = useState(false);
    const resetSearchAndFilters = () => {
        setSearch('');
        setFilters({
            date: null,
            locations: [],
            countries: []
        });
        setMeetings([]);
    };

    const searchByValue = (value) => {
        setSearch(value);
        setFilters({
            date: null,
            locations: [],
            countries: []
        });
        setMeetings([]);
    };

    const countValid = Object.values(filters).reduce((count, value) => {
        if (Array.isArray(value) && value.length > 0) {
            return count + 1;
        } else if ((typeof value === 'number' && value > -1) || typeof value === 'string') {
            return count + 1;
        }
        return count;
    }, 0);

    const changeFilter = (field, value) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [field]: value
        }));
    };

    const { data: meetingLocationsData } = useGetCalendarMeetingLocationsQuery({});

    const locations = useMemo(() => {
        if (meetingLocationsData?.response?.locations) {
            return meetingLocationsData.response.locations.map((location) => ({
                label: location,
                value: location
            }));
        }

        return [];
    }, [meetingLocationsData]);

    const { data: countriesData } = useGetMeetingCountriesQuery({});
    const countries = useMemo(() => {
        if (countriesData?.response?.meeting_countries) {
            return countriesData.response.meeting_countries.map((country) => ({
                label: country.name,
                value: country.id
            }));
        }

        return [];
    }, [countriesData]);

    const requestParams = useMemo(() => {
        let privateOrg = organizationSwitch[user?.id] ? organizationSwitch[user?.id].private : null;
        let organizations =
            organizationSwitch[user?.id] && organizationSwitch[user?.id].organizations
                ? Object.keys(organizationSwitch[user?.id].organizations).filter(
                      (key) => organizationSwitch[user?.id].organizations[key] === true
                  )
                : [];
        return {
            search: debouncedSearch,
            ...(filters.locations.length > 0 ? { locations: filters.locations } : {}),
            ...(filters.countries.length > 0 ? { countries: filters.countries } : {}),
            ...(filters.date !== null ? { date: filters.date } : {}),
            ...(organizations.length > 0 ? { organizations: organizations } : {}),
            private: privateOrg
        };
    }, [organizationSwitch, user?.id, debouncedSearch, filters.locations, filters.countries, filters.date]);

    const [advancedSearch] = useLazyCalendarMeetingAdvancedSearchQuery();

    const applyFilters = useCallback(async () => {
        if (requestParams.private || requestParams.organizations?.length > 0) {
            setIsLoading(true);
            const result = await advancedSearch(requestParams);
            if (result?.data?.response) {
                setMeetings(result.data.response);
            }
            setApplied(false);
            setIsLoading(false);
        }
    }, [advancedSearch, requestParams]);

    const simpleSelect = (field, item) => {
        return (
            <span
                key={`${field}-${item.name}`}
                className={`${filters[field] === item.value ? 'text-theme1-white' : 'text-theme1-primary'}`}
            >
                {item.name}
            </span>
        );
    };

    const renderCards = () => {
        const sections = [
            { key: 'title_query', label: 'Title' },
            { key: 'objectives_query', label: 'Objectives', textField: 'objectives' },
            { key: 'obstacles_query', label: 'Obstacles', textField: 'obstacles' },
            { key: 'context_query', label: 'Context', textField: 'context' }
        ];

        return sections.map((section) => (
            <div key={section.key} className='mb-6'>
                <BaseCard className='p-6 bg-white shadow-lg rounded-4xl border-0'>
                    <h2 className='text-theme1-text1 font-bold text-2xl mb-4'>{section.label}</h2>
                    {meetings[section.key]?.length > 0 &&
                    meetings[section.key].some((item) => Object.keys(item).length > 0) ? (
                        <>
                            <p className='text-theme1-text2 mb-4'>
                                {search !== '' ? (
                                    <>
                                        {meetings[section.key].length} meeting(s) with the {section.label.toLowerCase()}{' '}
                                        mentioning "{search}"
                                    </>
                                ) : (
                                    <>found {meetings[section.key].length} meeting(s) through filters</>
                                )}
                            </p>
                            {meetings[section.key].map((item, index) => (
                                <div key={index} className='border-t border-theme1-grey py-4'>
                                    <h3 className='text-lg text-theme1-success font-bold mb-1'>
                                        {renderFieldWithHighlighting(item.title, 'title', item.matched_fields)}
                                    </h3>
                                    {section.textField === 'objectives' &&
                                        item.objectives &&
                                        Object.keys(item.objectives).length > 0 && (
                                            <div>
                                                {item.objectives.map((objective, objIndex) => (
                                                    <p key={objective.id} className='text-theme1-text5 mb-1'>
                                                        {renderFieldWithHighlighting(
                                                            objective.text,
                                                            `objectives.text.${objIndex}`,
                                                            item.matched_fields
                                                        )}
                                                    </p>
                                                ))}
                                            </div>
                                        )}

                                    {section.textField === 'obstacles' &&
                                        item.obstacles &&
                                        Object.keys(item.obstacles).length > 0 && (
                                            <div>
                                                {item.obstacles.map((obstacle, obsIndex) => (
                                                    <p key={obstacle.id} className='text-theme1-text5 mb-1'>
                                                        {renderFieldWithHighlighting(
                                                            obstacle.text,
                                                            `obstacles.text.${obsIndex}`,
                                                            item.matched_fields
                                                        )}
                                                    </p>
                                                ))}
                                            </div>
                                        )}

                                    {section.textField === 'context' && (
                                        <p className='text-theme1-text5 mb-1'>
                                            {item.context.map((context, contextIndex) => (
                                                <span key={contextIndex} className='block text-theme1-text5 mb-1'>
                                                    {renderFieldWithHighlighting(
                                                        context,
                                                        `context.${contextIndex}`,
                                                        item.matched_fields
                                                    )}
                                                </span>
                                            ))}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </>
                    ) : (
                        <div className='text-gray-500'>No {section.label.toLowerCase()} found.</div>
                    )}
                </BaseCard>
            </div>
        ));
    };

    const config = [
        {
            field: 'date',
            type: 'singleselect',
            elements: simpleSelect,
            options: [
                { name: 'Past', value: 'past' },
                { name: 'Future', value: 'future' }
            ],
            title: 'Date'
        },
        {
            field: 'locations',
            options: locations,
            type: 'multiselect',
            title: 'Locations',
            placeholder: 'Select Locations'
        },
        {
            field: 'countries',
            options: countries,
            type: 'multiselect',
            title: 'Countries',
            placeholder: 'Select Countries'
        }
    ];

    useEffect(() => {
        if (!initialRender) {
            setApplied(true);
        }
    }, [initialRender, organizationSwitch]);

    useEffect(() => {
        if ((debouncedSearch !== '' && debouncedSearch === search) || (countValid > 0 && applied)) {
            setMeetings([]);
            setInitialRender(false);
            applyFilters().then();
        }

        if (countValid === 0) {
            setInitialRender(true);
        }
    }, [applyFilters, debouncedSearch, search, countValid, applied]);

    return (
        <AuthLayout title='Advanced Search' backRoute='/calendar'>
            <BaseCard className='border-0 m-0 mb-6 -mt-6 p-4 pt-10 rounded-t-none bg-theme1-white shadow-lg  max-w-3xl w-full mx-auto'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <div className='flex justify-between items-center mt-6 mb-4'>
                        <div className='flex-grow'>
                            <BaseInput
                                name='search'
                                type='text'
                                value={search}
                                placeholder='Search... (min 3 characters)'
                                setValue={(value) => searchByValue(value)}
                            />
                        </div>
                    </div>
                </div>
                <div className='mb-6 px-4 text-right'>
                    <div className='flex items-center w-full text-theme1-primary2'>
                        <div className='flex items-center space-x-3'>
                            <div className='flex items-center space-x-1'>
                                <BaseButton
                                    className='flex bg-theme1-lightBtns text-theme1-white p-3 pb-[10px]'
                                    onClick={() => setShowFilters(true)}
                                >
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='23'
                                        height='23'
                                        viewBox='0 0 20 20'
                                        fill='none'
                                    >
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M4.09254 3.2116C4.17673 3.18208 4.26531 3.16699 4.35454 3.16699H14.6462C14.7354 3.16699 14.824 3.18208 14.9082 3.2116C15.1362 3.29158 15.3429 3.42268 15.5125 3.59487C15.682 3.76706 15.8099 3.97578 15.8863 4.20502C15.9627 4.43426 15.9856 4.67795 15.9533 4.91743C15.921 5.15691 15.8343 5.38581 15.6999 5.5866C15.6782 5.61903 15.6541 5.64981 15.6278 5.67869L11.8754 9.80639V15.042C11.8754 15.3419 11.706 15.616 11.4377 15.7501C11.1695 15.8842 10.8486 15.8552 10.6087 15.6753L7.44204 13.3003C7.24269 13.1508 7.12537 12.9162 7.12537 12.667V9.80639L3.37292 5.67869C3.34666 5.64981 3.32258 5.61903 3.30087 5.5866C3.16643 5.38581 3.07973 5.15691 3.04742 4.91743C3.01511 4.67796 3.03804 4.43427 3.11445 4.20502C3.19087 3.97578 3.31874 3.76706 3.48828 3.59487C3.65781 3.42268 3.86451 3.29157 4.09254 3.2116ZM4.66876 4.75033L8.50282 8.96779C8.6353 9.11352 8.7087 9.30339 8.7087 9.50033V12.2712L10.292 13.4587V9.50033C10.292 9.30339 10.3654 9.11352 10.4979 8.96779L14.332 4.75033H4.66876Z'
                                            fill='white'
                                        />
                                    </svg>
                                    <span className='text-sm text-theme1-white px-2'>
                                        {countValid} filter(s) applied
                                    </span>
                                </BaseButton>
                            </div>
                        </div>

                        <BaseButton
                            className='ml-auto w-min rounded-4xl border font-normal btn-red-outline px-4 py-3'
                            onClick={() => resetSearchAndFilters()}
                        >
                            Reset
                        </BaseButton>
                    </div>
                    <MemoAdvancedSearchFilter
                        filters={filters}
                        setVisible={setShowFilters}
                        visible={showFilters}
                        config={config}
                        changeFilter={changeFilter}
                        applyFilters={applyFilters}
                        setApplied={setApplied}
                    ></MemoAdvancedSearchFilter>
                </div>
            </BaseCard>
            <div className='max-w-3xl w-full mx-auto px-4'>
                <div className='mb-6'>
                    {isLoading && <ThreeDotsLoader />}
                    {meetings.length === 0 && !isLoading && (
                        <div className='py-12 flex flex-col justify-center items-center'>
                            <div className='text-2xl text-theme1-primary2 font-light max-w-80 text-center'>
                                {countValid === 0 && search === ''
                                    ? 'Search and/or filter to display meetings!'
                                    : 'No meetings found!'}
                            </div>
                            <img alt='No meetings' src={circle} className='my-8 w-40 h-40' />
                        </div>
                    )}

                    {Object.values(meetings).some((value) => value.length > 0) && !isLoading && renderCards()}
                </div>
            </div>
        </AuthLayout>
    );
};

const MemoAdvancedSearchFilter = React.memo(AdvancedSearchFilter);
export default AdvancedSearchCalendarMeetings;
